import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import useScrollToTop from "@/hooks/useScrollToTop";
import { DASHBOARD_SIGNUP_URL } from "@/config/urls";

const FreeMusicDistribution = () => {
  useScrollToTop();
  
  useEffect(() => {
    document.title = "Free Music Distribution | Distribute Your Music for Free with YouAux";
  }, []);

  return (
    <div className="ophira-bg min-h-screen">
      <Helmet>
        <title>Free Music Distribution | Distribute Your Music for Free with YouAux</title>
        <meta
          name="description"
          content="Distribute your music to 150+ platforms worldwide. Upload once, distribute everywhere, and keep 100% ownership with YouAux."
        />
        <meta name="keywords" content="music distribution, streaming platforms, spotify distribution, apple music distribution, music marketing, artist promotion" />
        <link rel="canonical" href="https://youaux.com/free-music-distribution" />
      </Helmet>

      <Navbar />
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <div className="relative bg-gradient-to-r from-ophira-dark via-ophira-darkgray to-ophira-dark py-20 mb-16">
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">

              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent">
                YouAux: Free Music Distribution for Independent Artists
              </h1>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
            {/* Featured Image */}
            <div className="relative mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&h=400&fit=crop&crop=center" 
                alt="Free Music Distribution" 
                className="w-full h-64 md:h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex flex-wrap gap-2">
                  <span className="bg-ophira-yellow text-black px-3 py-1 rounded-full text-sm font-medium">Free Distribution</span>
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">100% Ownership</span>
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">150+ Platforms</span>
                </div>
              </div>
            </div>
          
          <div className="prose prose-invert max-w-none">
            {/* Introduction with decorative elements */}
            <div className="relative">
              <div className="absolute -left-8 top-0 w-1 h-full bg-gradient-to-b from-ophira-yellow to-transparent"></div>
              <p className="text-lg mb-6 leading-relaxed">
              Distribute your music for free with YouAux, the trusted platform for independent artists who want their music on major streaming services without the fees. Our free music distribution model empowers creators by putting their music on Spotify, Apple Music, Amazon Music, and 150+ platforms at zero cost.
            </p>
            </div>
            
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mt-12 mb-8">
              Why Choose YouAux for Free Music Distribution?
            </h2>
            
            <div className="space-y-8">
              <div className="group bg-gradient-to-r from-ophira-dark/80 to-ophira-darkgray/50 p-8 rounded-2xl border border-ophira-yellow/20 hover:border-ophira-yellow/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-ophira-yellow/5 rounded-full -translate-y-10 translate-x-10"></div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-ophira-yellow">1. Distribute Music for Free on 150+ Platforms</h3>
                  <p className="text-ophira-lightgray leading-relaxed">YouAux offers music distribution for free, ensuring your tracks reach global audiences through services like Spotify, Apple Music, Amazon Music, YouTube Music, and more without charging a dime.</p>
                </div>
              </div>
              
              <div className="group bg-gradient-to-r from-ophira-dark/80 to-ophira-darkgray/50 p-8 rounded-2xl border border-ophira-yellow/20 hover:border-ophira-yellow/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-ophira-yellow/5 rounded-full -translate-y-10 translate-x-10"></div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-ophira-yellow">2. Keep 100% Ownership</h3>
                  <p className="text-ophira-lightgray leading-relaxed">We're one of the few free music distributors that lets you keep all your ownership. No hidden fees, no commission cuts, your earnings belong to you.</p>
                </div>
              </div>
              
              <div className="group bg-gradient-to-r from-ophira-dark/80 to-ophira-darkgray/50 p-8 rounded-2xl border border-ophira-yellow/20 hover:border-ophira-yellow/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-ophira-yellow/5 rounded-full -translate-y-10 translate-x-10"></div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-ophira-yellow">3. Global Reach with Free Music Distribution</h3>
                  <p className="text-ophira-lightgray leading-relaxed">With free distribution music, your songs will be heard across the globe. Our platform gives you access to millions of listeners without borders or extra charges.</p>
                </div>
              </div>
              
              <div className="group bg-gradient-to-r from-ophira-dark/80 to-ophira-darkgray/50 p-8 rounded-2xl border border-ophira-yellow/20 hover:border-ophira-yellow/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-ophira-yellow/5 rounded-full -translate-y-10 translate-x-10"></div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-ophira-yellow">4. Fast & Simple Upload Process</h3>
                  <p className="text-ophira-lightgray leading-relaxed">Upload your music, cover art, and metadata in minutes. Our platform is user-friendly and optimized for quick distribution.</p>
                </div>
              </div>
              
              <div className="group bg-gradient-to-r from-ophira-dark/80 to-ophira-darkgray/50 p-8 rounded-2xl border border-ophira-yellow/20 hover:border-ophira-yellow/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-ophira-yellow/5 rounded-full -translate-y-10 translate-x-10"></div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-ophira-yellow">5. Real-Time Music Analytics</h3>
                  <p className="text-ophira-lightgray leading-relaxed">Track your success with our comprehensive analytics dashboard. Know where your listeners are, which platforms drive engagement, and how your music is performing overall.</p>
                </div>
              </div>
            </div>
            
            {/* Decorative separator */}
            <div className="flex items-center gap-4 my-16">
              <div className="h-px bg-gradient-to-r from-transparent via-ophira-yellow to-transparent flex-1"></div>
              <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
              <div className="h-px bg-gradient-to-r from-transparent via-ophira-yellow to-transparent flex-1"></div>
            </div>

            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mb-8">
              Key Features of YouAux Free Music Distribution
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">Global Reach</h3>
                </div>
                <p className="text-ophira-lightgray">Distribute music to over 150 platforms worldwide.</p>
              </div>
              
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">No Upfront Fees</h3>
                </div>
                <p className="text-ophira-lightgray">True free music distribution, no subscriptions or service charges.</p>
              </div>
              
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">Full Royalties</h3>
                </div>
                <p className="text-ophira-lightgray">Artists keep 100% of the revenue.</p>
              </div>
              
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">Video Distribution</h3>
                </div>
                <p className="text-ophira-lightgray">Submit music videos to platforms like YouTube and Vevo.</p>
              </div>
              
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">Fast Delivery</h3>
                </div>
                <p className="text-ophira-lightgray">Get your music live in days not weeks.</p>
              </div>
              
              <div className="bg-gradient-to-br from-ophira-dark/60 to-ophira-darkgray/40 p-6 rounded-xl border border-ophira-yellow/10 hover:border-ophira-yellow/30 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-ophira-yellow rounded-full"></div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-ophira-yellow transition-colors">Royalty Tracking</h3>
                </div>
                <p className="text-ophira-lightgray">Accurate and timely payments through our automated system.</p>
              </div>
            </div>
            
            {/* How it works section with visual steps */}
            <div className="bg-gradient-to-r from-ophira-dark/40 via-ophira-darkgray/20 to-ophira-dark/40 p-8 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mb-8">
                How Free Music Distribution with YouAux Works
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-ophira-yellow text-black w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <strong className="text-white">Create Your Free Account</strong>
                    <span className="text-ophira-lightgray"> on YouAux.</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ophira-yellow text-black w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <strong className="text-white">Upload Your Music</strong>
                    <span className="text-ophira-lightgray">, cover art, and details.</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ophira-yellow text-black w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <strong className="text-white">Select Your Platforms</strong>
                    <span className="text-ophira-lightgray"> - Spotify, Apple Music, Deezer, and more.</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ophira-yellow text-black w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <strong className="text-white">Distribute for Free</strong>
                    <span className="text-ophira-lightgray"> in just a few clicks.</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ophira-yellow text-black w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
                  <div>
                    <strong className="text-white">Track Results</strong>
                    <span className="text-ophira-lightgray"> with our real-time analytics tools.</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Why it matters section */}
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mt-16 mb-8">
              Why Free Music Distribution Matters for Independent Artists
            </h2>
            
            <div className="bg-gradient-to-r from-ophira-yellow/5 to-transparent p-6 rounded-xl border-l-4 border-ophira-yellow mb-8">
              <p className="text-ophira-lightgray leading-relaxed mb-4">For indie artists, free music distribution to all platforms is a game changer. YouAux breaks down financial barriers, so you can focus on what matters - creating music, connecting with fans, and growing your career.</p>
              <p className="text-ophira-lightgray leading-relaxed">Unlike traditional services, we offer free music distribution with no strings attached. Whether you're just starting out or already have a following, our tools give you the freedom to thrive.</p>
            </div>
            
            {/* Benefits section */}
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mt-12 mb-8">
              Benefits of Choosing YouAux
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
              <div className="flex items-start gap-3 p-4 bg-ophira-dark/30 rounded-lg">
                <div className="w-2 h-2 bg-ophira-yellow rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-white">No Fees</strong>
                  <span className="text-ophira-lightgray"> – 100% free music distribution</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-ophira-dark/30 rounded-lg">
                <div className="w-2 h-2 bg-ophira-yellow rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-white">Full Music Rights</strong>
                  <span className="text-ophira-lightgray"> – Retain complete ownership</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-ophira-dark/30 rounded-lg">
                <div className="w-2 h-2 bg-ophira-yellow rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-white">Global Exposure</strong>
                  <span className="text-ophira-lightgray"> – 150+ platforms</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-ophira-dark/30 rounded-lg">
                <div className="w-2 h-2 bg-ophira-yellow rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-white">Artist-Focused Tools</strong>
                  <span className="text-ophira-lightgray"> – Powerful analytics and video support</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-ophira-dark/30 rounded-lg md:col-span-2">
                <div className="w-2 h-2 bg-ophira-yellow rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <strong className="text-white">Quick Setup</strong>
                  <span className="text-ophira-lightgray"> – Upload and distribute in minutes</span>
                </div>
              </div>
            </div>
            
            {/* Call to action section */}
            <div className="relative bg-gradient-to-r from-ophira-yellow/10 via-ophira-yellow/5 to-transparent p-12 rounded-2xl text-center overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-ophira-yellow/5 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-ophira-yellow/10 rounded-full translate-y-10 -translate-x-10"></div>
              
              <div className="relative z-10">
                <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent">
                  Get Started Today – Distribute Music for Free with YouAux
                </h2>
                <p className="text-lg text-ophira-lightgray mb-8 max-w-2xl mx-auto leading-relaxed">
                  Don't let distribution costs limit your reach. Join YouAux today and start distributing your music for free. Whether you're a new artist or an industry veteran, our free music distribution platform puts your music on the map globally and affordably.
                </p>
                
                              <a
                  href={DASHBOARD_SIGNUP_URL}
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-500/25"
                >
                  Sign Up Now and Let Your Sound Be Heard
                </a>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-ophira-yellow bg-clip-text text-transparent mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Is YouAux really free to use?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Yes. YouAux offers 100% free music distribution with no upfront fees, subscriptions, or hidden charges. You keep all of your royalties without any commission cuts.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Which platforms will my music be distributed to?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Your music will be available on 150+ platforms worldwide, including Spotify, Apple Music, Amazon Music, YouTube Music, Deezer, and many more.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Do I keep 100% of my ownership?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Absolutely. YouAux ensures that you keep 100% of ownership of your music. 
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>How long does it take for my music to go live on YouAux?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Once uploaded, your music is typically live on platforms within just a 4-5 days, not weeks.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Can I distribute my music videos too?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Yes. YouAux supports video distribution to platforms such as YouTube and Vevo.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>How do I upload my music on YouAux?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    It’s simple. Create a free YouAux account, upload your music files, cover art, and details, select your desired platforms, and distribute with just a few clicks.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Who can use YouAux?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    YouAux is built for independent artists at any stage of their career from emerging creators to experienced musicians who want global reach without the cost.
                  </div>
                </details>
                <details className="group bg-ophira-darkgray/40 rounded-2xl p-5 border border-ophira-darkgray/50">
                  <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                    <span>Why should I choose YouAux over other music distributors?</span>
                    <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="mt-3 text-ophira-lightgray">
                    Unlike most distributors, YouAux offers free distribution, full royalty retention, fast delivery and 24/7 support. all in one platform designed with independent artists in mind.
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default FreeMusicDistribution;
